/**
 * Minimal word-level diff (longest common subsequence) for showing what a review changed
 * in a prompt. Prompts are at most 8000 characters, so the O(n·m) table is fine.
 */
export type DiffOp = { kind: 'same' | 'add' | 'del'; text: string }

const tokenize = (s: string) => s.match(/\s+|[^\s]+/g) ?? []

export function wordDiff(before: string, after: string): DiffOp[] {
  const a = tokenize(before)
  const b = tokenize(after)
  const n = a.length
  const m = b.length
  // lcs[i][j] = length of LCS of a[i:] and b[j:]
  const lcs: Uint16Array[] = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1))
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1])
    }
  }
  const ops: DiffOp[] = []
  const push = (kind: DiffOp['kind'], text: string) => {
    const last = ops[ops.length - 1]
    if (last && last.kind === kind) last.text += text
    else ops.push({ kind, text })
  }
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (a[i] === b[j]) { push('same', a[i]); i++; j++ }
    else if (lcs[i + 1][j] >= lcs[i][j + 1]) { push('del', a[i]); i++ }
    else { push('add', b[j]); j++ }
  }
  while (i < n) push('del', a[i++])
  while (j < m) push('add', b[j++])
  return ops
}

export const hasChanges = (ops: DiffOp[]) => ops.some((o) => o.kind !== 'same' && o.text.trim() !== '')
